/**
 * The application shell.
 *
 * Owns the three-zone terminal layout (rail / content / mobile bar), the
 * global keyboard map, the LIVE banner and the live cache sync.
 *
 * The LIVE banner is rendered *outside* the scroll container and is not
 * dismissible. A banner you can close is a banner that will be closed on
 * the day it matters.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEnvironment, useLiveCacheSync } from '@/api/hooks';
import { cn } from '@/lib/cn';
import { themeStore } from '@/theme/ThemeProvider';

import { LiveBanner } from '../ui/Badge';
import { ToastViewport } from '../ui/Toast';
import { MobileDrawer, MobileNav } from './MobileNav';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { SHOWN_NAV_ITEMS } from './navigation';

/**
 * Global keyboard map.
 *
 * `g` then a letter navigates (vi-style, so it never collides with typing).
 * `t` toggles the theme. `Shift+H` focuses the halt button — deliberately
 * *focus*, not fire: a global hotkey that instantly stops all trading is
 * one fat-finger away from being a problem of its own.
 *
 * Every binding is suppressed while a text field has focus.
 */
function useGlobalHotkeys(): void {
  const navigate = useNavigate();

  useEffect(() => {
    let pendingGoto = false;
    let gotoTimer: number | null = null;

    const isTyping = (): boolean => {
      const active = document.activeElement;
      return (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      );
    };

    const clearGoto = (): void => {
      pendingGoto = false;
      if (gotoTimer !== null) {
        window.clearTimeout(gotoTimer);
        gotoTimer = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (isTyping() || event.metaKey || event.ctrlKey || event.altKey) return;

      if (pendingGoto) {
        const target = SHOWN_NAV_ITEMS.find((item) => item.hotkey === event.key);
        clearGoto();
        if (target) {
          event.preventDefault();
          void navigate(target.to);
        }
        return;
      }

      if (event.key === 'g') {
        pendingGoto = true;
        // A 1.5s window: long enough to be forgiving, short enough that a
        // stray `g` does not swallow the next real keystroke.
        gotoTimer = window.setTimeout(clearGoto, 1500);
        return;
      }

      if (event.key === 't') {
        event.preventDefault();
        themeStore.toggle();
        return;
      }

      if (event.key === 'H' && event.shiftKey) {
        event.preventDefault();
        document
          .querySelector<HTMLButtonElement>('[data-hotkey="halt"]')
          ?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearGoto();
    };
  }, [navigate]);
}

export interface AppShellProps {
  children: ReactNode;
  /** Page-supplied controls for the middle of the top bar. */
  topBarContent?: ReactNode;
  /** Turn off the content container's padding — for full-bleed chart pages. */
  flush?: boolean;
}

export function AppShell({ children, topBarContent, flush = false }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: environment } = useEnvironment();

  useGlobalHotkeys();
  useLiveCacheSync();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-0 text-hi">
      {environment?.is_live && <LiveBanner venue={environment.venue} />}

      <TopBar onOpenMenu={() => setDrawerOpen(true)}>{topBarContent}</TopBar>

      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <main
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-hidden',
            flush ? '' : 'gap-2 overflow-y-auto p-2 sm:p-3',
          )}
        >
          {children}
        </main>
      </div>

      <MobileNav />
      <MobileDrawer open={drawerOpen} onClose={closeDrawer} />
      <ToastViewport />
    </div>
  );
}

/**
 * Standard page frame.
 *
 * Gives every page the same title/description/actions header so they do not
 * each invent one. Pages that are entirely chart (Markets) skip it.
 */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex shrink-0 items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h1 className="truncate text-[15px] font-semibold text-hi md:text-[14px]">{title}</h1>
        {description && (
          <p className="mt-0.5 text-[11.5px] leading-snug text-lo">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  );
}
